import React, {useState} from 'react';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
} from '@mui/material';
import {useChatContext} from '../context';
import {Chat} from '@services/Chat/types';
import {ContactOptionItem} from './ContactOptionItem';
import Close from '@mui/icons-material/Close';
import {GroupChatUsersList} from './GroupChatUsersList';
import {LightTooltip} from '@ui/LightTooltip/LightTooltip';
import {useTranslation} from 'react-i18next';

export const GroupChatCreateModal = ({
    selectedChat,
    createMode,
}: {
    selectedChat?: Chat | null;
    createMode?: boolean;
}) => {
    const {chatUsers, getGroupChat, editGroupChat, chatInfo, currentUser} = useChatContext();

    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const canEdit = currentUser?.username === chatInfo?.chatOwner;

    const handleOpen = () => {
        if (chatInfo && !createMode) {
            setTitle(chatInfo.title);
            const initialUsers = chatInfo.userList
                ?.map((u) => chatUsers?.find((opt) => opt.toLowerCase() === u.toLowerCase()))
                .filter((u): u is string => Boolean(u));

            setSelectedUsers(initialUsers ?? []);
        } else {
            setTitle('');
            setSelectedUsers([]);
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || selectedUsers.length === 0) return;

        if (selectedChat) {
            editGroupChat(selectedChat.id, title.trim(), selectedUsers);
        } else {
            getGroupChat(selectedUsers, title.trim());
        }
        handleClose();
    };

    const {t} = useTranslation(['header', 'common']);

    return (
        <>
            <LightTooltip title={createMode ? t('chat.createGroupChat') : t('chat.editGroupChat')}>
                <IconButton
                    onClick={handleOpen}
                    sx={{maxWidth: '400px'}}
                >
                    {selectedChat ? (
                        <ContactOptionItem
                            option={selectedChat}
                            size={32}
                        />
                    ) : (
                        <GroupAddOutlinedIcon />
                    )}
                </IconButton>
            </LightTooltip>

            <Dialog
                open={open}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') {
                        return;
                    }
                    handleClose();
                }}
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle sx={{display: 'flex', justifyContent: 'space-between'}}>
                    {selectedChat ? t('chat.editGroupChat') : t('chat.createGroupChat')}
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        <TextField
                            disabled={!canEdit && !createMode}
                            size='small'
                            label={t('chat.groupChatName')}
                            fullWidth
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            slotProps={{
                                htmlInput: {maxLength: 60},
                            }}
                        />
                        <GroupChatUsersList
                            selectedUsers={selectedUsers}
                            onChange={setSelectedUsers}
                            canEdit={canEdit}
                            createMode={createMode}
                        />
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleClose}>{t('cancel')}</Button>
                        <Button
                            disabled={!title.trim() || selectedUsers.length === 0}
                            type='submit'
                            variant='contained'
                        >
                            {selectedChat ? t('save') : t('create')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
};
